/**
 * orgDashboardEvents — client-side PostHog events that say which parts of the
 * org dashboard facilitators actually use (#4258, Part-of #2033).
 *
 * WHY: before this, tab switches, filter changes, nudges, reports, exports,
 * Ask Praxis questions and learner-detail opens were not captured at all, so
 * section use could only be inferred from pageviews. The redesign (#2033,
 * #3890) therefore had no "before" to be measured against.
 *
 * LOOP CLOSURE (CLAUDE.md "Loop Closure"):
 *   writer:           the seven events below (call sites are listed per event).
 *   scheduled reader: two docs/DATED-CHECKS.md rows (deploy+7d census, deploy+28d
 *                     per-distinct-id breakdown), owned by `dated-checks-daily`.
 *                     NOT YET FILED as of #4433 (no row names these events): the
 *                     rows are dated from the prod deploy, so they are filed at
 *                     deploy time; until then this stream has no scheduled reader.
 *   what changes:     which dashboard sections the redesign keeps, cuts or
 *                     promotes; an event with 0 rows, or a property with one
 *                     distinct value, is a DEAD INSTRUMENT and reopens #4258.
 *
 * Already instrumented elsewhere (do NOT duplicate here): kudos_sent,
 * kudos_sent_with_note, kudos_batch_sent (server, routes/recognitions.js),
 * kudos_batch_sent_client, highlight_dismissed, notable_feed_impression,
 * notable_feed_clicked (NotableActivityFeed / NotableLearnerGroupCard).
 *
 * PRIVACY: properties are enums, counts and lengths ONLY. Never a learner name,
 * email, free text or question text (learner-data egress). The distinct id is the
 * integer user id PostHog already has from posthog.identify(user.id).
 *
 * ABSENCE: an unknown organisation is the explicit string 'not_set', never a
 * missing key, so "no org" cannot read as a healthy value.
 *
 * FAIL-OPEN: PostHog is a commonly blocked origin. A throwing or absent
 * posthog must never break the tab switch, send, download or page it observes.
 */
import { useCallback, useRef } from 'react';
import { posthog } from '../../main';
import { useAuth } from '../../contexts/AuthContext';

export const ORG_DASHBOARD_EVENTS = Object.freeze({
  TAB_VIEWED: 'org_dashboard_tab_viewed', // {tab, entry: 'click'|'url'|'default'}
  FILTER_CHANGED: 'org_dashboard_filter_changed', // {filter: 'group'|'week', value_count}
  NUDGE_SENT: 'org_dashboard_nudge_sent', // {kind}
  REPORT_GENERATED: 'report_generated', // {type}
  EXPORT_DOWNLOADED: 'export_downloaded', // {type}
  ASK_PRAXIS_QUESTION_ASKED: 'ask_praxis_question_asked', // {question_length}
  LEARNER_DETAIL_OPENED: 'learner_detail_opened', // {source}
});

/** Value written for an organisation id (or any enum) that is genuinely unknown. */
export const NOT_SET = 'not_set';

/**
 * Capture one org-dashboard event with `organization_id` attached.
 * Never throws. `organization_id` is applied last so a caller cannot shadow it.
 *
 * @param {string} name   one of ORG_DASHBOARD_EVENTS
 * @param {object} [props] enums / counts / lengths only
 * @param {number|string|null} [organizationId] the viewer's organisation id
 */
export function captureOrgDashboardEvent(name, props, organizationId) {
  try {
    if (!posthog || typeof posthog.capture !== 'function') return;
    posthog.capture(name, {
      ...(props || {}),
      organization_id: organizationId === undefined || organizationId === null ? NOT_SET : organizationId,
    });
  } catch {
    // Telemetry must never break the action it observes.
  }
}

// useAuth() throws outside an AuthProvider (isolated renders, static-markup
// tests). Analytics must not turn that into a render failure: the context read
// still happens on every render, so hook order is stable either way.
function useOptionalOrganizationId() {
  try {
    const auth = useAuth();
    return auth?.user?.organization_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Returns a referentially stable `(name, props) => void` bound to the viewer's
 * organisation. Stable identity lets effects and callbacks list it as a
 * dependency without re-running when the organisation id is refreshed.
 */
export function useOrgDashboardCapture() {
  const organizationId = useOptionalOrganizationId();
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;
  return useCallback(
    (name, props) => captureOrgDashboardEvent(name, props, organizationIdRef.current),
    [],
  );
}

// Mirrors OrgDashboardPage's TAB_KEYS (kept separate: that page is held by #4113).
export const ORG_DASHBOARD_TABS = Object.freeze([
  'highlights', 'activity', 'users', 'performance', 'scenarios', 'devices', 'preferences',
]);

/**
 * Map the router `state.from` the dashboard passes when opening a learner into a
 * small closed enum for learner_detail_opened.source:
 *   org_dashboard_<tab>   from /org-dashboard?tab=<known tab>, or (when there is
 *                         no ?tab=) /org-dashboard#<known tab>: the Highlights
 *                         sections (ProgressStories, ScoreImprovementArcs,
 *                         PathwayTrajectoryArcs) pass the hash form (#4433)
 *   org_dashboard         from /org-dashboard with no/unknown tab (role default)
 *   learner_progress      from /learner-progress...
 *   other                 any other in-app origin
 *   direct                no state.from (deep link, reload, external)
 * Never returns the raw path (it can carry ids and query strings).
 *
 * `highlights` vs `notable_feed` cannot be told apart from `from` alone (both
 * arrive as /org-dashboard?tab=highlights); the lane after the first read adds
 * `state` at those sites only if the census shows it matters.
 */
export function learnerDetailSourceFromState(from) {
  if (typeof from !== 'string' || from.length === 0) return 'direct';
  // Split the fragment off FIRST (#4433): a '#' after the path would otherwise
  // leave '/org-dashboard#highlights' as the "path" and fall through to 'other'.
  const hashIndex = from.indexOf('#');
  const fragment = hashIndex === -1 ? '' : from.slice(hashIndex + 1);
  const beforeHash = hashIndex === -1 ? from : from.slice(0, hashIndex);
  const qIndex = beforeHash.indexOf('?');
  const path = qIndex === -1 ? beforeHash : beforeHash.slice(0, qIndex);
  const query = qIndex === -1 ? '' : beforeHash.slice(qIndex + 1);
  if (path === '/org-dashboard') {
    let tab = null;
    try {
      tab = new URLSearchParams(query).get('tab');
    } catch {
      tab = null;
    }
    // The fragment names a tab only when the query carries no ?tab= at all.
    if (tab === null) tab = fragment;
    return ORG_DASHBOARD_TABS.includes(tab) ? `org_dashboard_${tab}` : 'org_dashboard';
  }
  if (path === '/learner-progress' || path.startsWith('/learner-progress/')) return 'learner_progress';
  return 'other';
}
